import {
  CustoProjetoStatus,
  EtapaAprovacaoStatus,
  JobStatus,
  VersaoStatus,
  VersaoTipo,
  CompraStatus,
  Empresa,
  FaturamentoTipo,
  FaturamentoStatus,
  JobPerfil,
  JobTipo,
  FaturavelTipo,
  JobPlanejamentoStatus,
  TarefaStatus,
  TarefaTipo,
  BudgetStatus,
  StatusProcessamentoIA,
  PlanilhaOrdemJob,
  PlanilhaCompraTipo,
  AbrangenciaNivelEnum,
  ApStatus,
  CustoBudgetVersaoStatus,
} from "@workflow/types";

export const JOB_STATUS_COLOR_MAP: Record<JobStatus, string> = {
  [JobStatus.EM_ANDAMENTO]:
    "bg-amber-50 text-amber-700 border border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800",

  [JobStatus.EM_BRIEFING]:
    "bg-violet-50 text-violet-700 border border-violet-200 dark:bg-violet-900/30 dark:text-violet-300 dark:border-violet-800",

  [JobStatus.EM_PROJETO]:
    "bg-sky-50 text-sky-700 border border-sky-200 dark:bg-sky-900/30 dark:text-sky-300 dark:border-sky-800",

  [JobStatus.ENCERRADO]:
    "bg-slate-100 text-slate-700 border border-slate-300 dark:bg-slate-800/50 dark:text-slate-400 dark:border-slate-700",
};

export const JOB_PERFIL_COLOR_MAP: Record<JobPerfil, string> = {
  [JobPerfil.ESTRATEGICO]:
    "bg-indigo-50 text-indigo-700 border border-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-300 dark:border-indigo-800",

  [JobPerfil.TATICO]:
    "bg-amber-50 text-amber-700 border border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800",

  [JobPerfil.OPERACIONAL]:
    "bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800",

  [JobPerfil.INSTITUCIONAL]:
    "bg-rose-50 text-rose-700 border border-rose-200 dark:bg-rose-900/30 dark:text-rose-300 dark:border-rose-800",
};

/**
 * Cores dos marcos da agenda (briefing/paper/entrega). Mesma convenção de classes
 * dos demais `*_COLOR_MAP` — consumido pela `AgendaCategoriaConfig` do adapter de
 * jobs. Chaveado por string (`TipoMarco`) para não acoplar este arquivo ao enum.
 */
export const AGENDA_MARCO_COLOR_MAP: Record<string, string> = {
  // Hues bem espaçados no círculo cromático (sky → amber → fuchsia → emerald)
  // para que os 4 marcos sejam distinguíveis de relance, mesmo na faixa fina.
  CRIACAO:
    "bg-sky-50 text-sky-700 border border-sky-200 dark:bg-sky-950/40 dark:text-sky-300 dark:border-sky-800/60",

  BRIEFING:
    "bg-amber-50 text-amber-700 border border-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-800/60",

  PAPER:
    "bg-rose-50 text-rose-700 border border-rose-200 dark:bg-rose-950/40 dark:text-rose-300 dark:border-rose-800/60",

  ENTREGA:
    "bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800/60",

  // Teal, não violeta: `JobStatus.EM_BRIEFING` já é violeta, e os dois convivem no mesmo
  // tooltip de marco (faixa do tipo + pill de status) — precisam ser visualmente distintos.
  EVENTO:
    "bg-teal-50 text-teal-700 border border-teal-200 dark:bg-teal-950/40 dark:text-teal-300 dark:border-teal-800/60",
};

export const JOB_TIPO_COLOR_MAP: Record<JobTipo, string> = {
  [JobTipo.ATIVACAO]:
    "bg-red-50 text-red-700 border border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800",

  [JobTipo.BRINDES]:
    "bg-sky-50 text-sky-700 border border-sky-200 dark:bg-sky-900/30 dark:text-sky-300 dark:border-sky-800",

  [JobTipo.DIVERSOS]:
    "bg-amber-50 text-amber-700 border border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800",

  [JobTipo.EVENTO_FISICO]:
    "bg-orange-50 text-orange-700 border border-orange-200 dark:bg-orange-900/30 dark:text-orange-300 dark:border-orange-800",

  [JobTipo.FEE]:
    "bg-zinc-100 text-zinc-800 border border-zinc-300 dark:bg-zinc-800 dark:text-zinc-300 dark:border-zinc-700",

  [JobTipo.INCENTIVO]:
    "bg-teal-50 text-teal-700 border border-teal-200 dark:bg-teal-900/30 dark:text-teal-300 dark:border-teal-800",

  [JobTipo.MATERIAIS]:
    "bg-violet-50 text-violet-700 border border-violet-200 dark:bg-violet-900/30 dark:text-violet-300 dark:border-violet-800",

  [JobTipo.MPDV]:
    "bg-fuchsia-50 text-fuchsia-700 border border-fuchsia-200 dark:bg-fuchsia-900/30 dark:text-fuchsia-300 dark:border-fuchsia-800",

  [JobTipo.PROMOCAO]:
    "bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800",

  [JobTipo.TRIANGULACAO]:
    "bg-slate-100 text-slate-700 border border-slate-300 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700",
};

export const BUDGET_STATUS_COLOR_MAP: Record<BudgetStatus, string> = {
  Aprovado:
    "bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800",

  "Em Aprovação":
    "bg-amber-50 text-amber-700 border border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800",

  "Em Aprovação Quente":
    "bg-orange-50 text-orange-700 border border-orange-200 dark:bg-orange-900/30 dark:text-orange-300 dark:border-orange-800",

  Faturando:
    "bg-violet-50 text-violet-700 border border-violet-200 dark:bg-violet-900/30 dark:text-violet-300 dark:border-violet-800",

  "Faturando Parcialmente":
    "bg-violet-100 text-violet-800 border border-violet-300 dark:bg-violet-900/50 dark:text-violet-300 dark:border-violet-700",

  Faturado:
    "bg-sky-50 text-sky-700 border border-sky-200 dark:bg-sky-900/30 dark:text-sky-300 dark:border-sky-800",

  "Faturado Parcialmente":
    "bg-sky-100 text-sky-800 border border-sky-300 dark:bg-sky-900/50 dark:text-sky-300 dark:border-sky-700",

  Cancelado:
    "bg-red-50 text-red-700 border border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800",

  Declinado:
    "bg-red-50 text-red-700 border border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800",

  Perdido:
    "bg-red-50 text-red-700 border border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800",

  Reestruturado:
    "bg-red-50 text-red-700 border border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800",

  Isento: "bg-purple-200 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300",
};

export const FATURAVEL_TIPO_COLOR_MAP: Record<FaturavelTipo, string> = {
  [FaturavelTipo.ATENDIMENTO]:
    "bg-sky-50 text-sky-700 border border-sky-200 dark:bg-sky-900/30 dark:text-sky-300 dark:border-sky-800",

  [FaturavelTipo.ATIVACAO]:
    "bg-indigo-50 text-indigo-700 border border-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-300 dark:border-indigo-800",

  [FaturavelTipo.FEE]:
    "bg-violet-50 text-violet-700 border border-violet-200 dark:bg-violet-900/30 dark:text-violet-300 dark:border-violet-800",

  [FaturavelTipo.INCENTIVO]:
    "bg-teal-50 text-teal-700 border border-teal-200 dark:bg-teal-900/30 dark:text-teal-300 dark:border-teal-800",

  [FaturavelTipo.MATERIAIS]:
    "bg-amber-50 text-amber-700 border border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800",

  [FaturavelTipo.MPDV]:
    "bg-fuchsia-50 text-fuchsia-700 border border-fuchsia-200 dark:bg-fuchsia-900/30 dark:text-fuchsia-300 dark:border-fuchsia-800",

  [FaturavelTipo.PROMOCAO]:
    "bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800",

  [FaturavelTipo.TRIANGULACAO]:
    "bg-slate-100 text-slate-700 border border-slate-300 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700",

  [FaturavelTipo.BRINDES]:
    "bg-rose-50 text-rose-700 border border-rose-200 dark:bg-rose-900/30 dark:text-rose-300 dark:border-rose-800",

  [FaturavelTipo.DIVERSOS]:
    "bg-lime-50 text-lime-700 border border-lime-200 dark:bg-lime-900/30 dark:text-lime-300 dark:border-lime-800",

  [FaturavelTipo.EVENTO_FISICO]:
    "bg-orange-50 text-orange-700 border border-orange-200 dark:bg-orange-900/30 dark:text-orange-300 dark:border-orange-800",
};

// Nota: Para os itens abaixo que não tinham borda original, mantive sem borda
// mas ajustei o background para ser mais visível ou sutil conforme necessário no dark.

export const ETAPA_APROVACAO_STATUS_COLOR_MAP: Record<EtapaAprovacaoStatus, string> = {
  [EtapaAprovacaoStatus.PENDENTE]:
    "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300",
  [EtapaAprovacaoStatus.AGUARDANDO]:
    "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300",
  [EtapaAprovacaoStatus.APROVADO]:
    "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300",
  [EtapaAprovacaoStatus.REPROVADO]: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300",
  [EtapaAprovacaoStatus.AUSENTE]: "bg-blue-300 text-blue-800 dark:bg-blue-800 dark:text-blue-200",
  [EtapaAprovacaoStatus.ABORTADO]: "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300",
  [EtapaAprovacaoStatus.CANCELADO]: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300",
  [EtapaAprovacaoStatus.DESAPROVADO]:
    "bg-red-200 text-red-800 dark:bg-red-900/60 dark:text-red-200",
};

export const VERSAO_STATUS_COLOR_MAP: Record<VersaoStatus, string> = {
  [VersaoStatus.RASCUNHO]: "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300",
  [VersaoStatus.AGUARDANDO]: "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300",
  [VersaoStatus.APROVADO]: "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300",
  [VersaoStatus.REPROVADO]: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300",
  [VersaoStatus.APROVADO_INTERNAMENTE]:
    "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300",
  [VersaoStatus.APROVADO_PARCIALMENTE]:
    "bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300",
  [VersaoStatus.ENVIADO_CLIENTE]:
    "bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300",
  [VersaoStatus.CANCELADO]: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300",
  [VersaoStatus.DESAPROVADO]: "bg-red-200 text-red-800 dark:bg-red-900/60 dark:text-red-200",
  [VersaoStatus.EM_APROVACAO]:
    "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300",
  [VersaoStatus.EM_REVISAO]:
    "bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300",
  [VersaoStatus.REPROVADO_CLIENTE]: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300",
};

export const VERSAO_TIPO_COLOR_MAP: Record<VersaoTipo, string> = {
  [VersaoTipo.ATENDIMENTO]:
    "bg-sky-50 text-sky-700 border border-sky-200 dark:bg-sky-900/30 dark:text-sky-300 dark:border-sky-800",

  [VersaoTipo.CLIENTE]:
    "bg-sky-50 text-sky-700 border border-sky-200 font-medium dark:bg-sky-900/30 dark:text-sky-300 dark:border-sky-800",

  [VersaoTipo.COMPRAS]:
    "bg-amber-50 text-amber-700 border border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800",

  [VersaoTipo.INTERNO]:
    "bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800",
};

export const COMPRA_STATUS_COLOR_MAP: Record<CompraStatus, string> = {
  [CompraStatus.EM_NEGOCIACAO]:
    "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300",
  [CompraStatus.NEGOCIADO]: "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300",
  [CompraStatus.SEM_OPORTUNIDADE]: "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300",
};

export const FATURAMENTO_STATUS_COLOR_MAP: Record<FaturamentoStatus | "ISENTO", string> = {
  [FaturamentoStatus.PENDENTE]:
    "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300",
  [FaturamentoStatus.FATURANDO]:
    "bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300",
  [FaturamentoStatus.FATURADO]: "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300",
  [FaturamentoStatus.PAGO]: "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300",
  [FaturamentoStatus.REPROVADO]: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300",
  [FaturamentoStatus.DESFEITO]: "bg-red-200 text-red-800 dark:bg-red-900/60 dark:text-red-200",
  [FaturamentoStatus.CANCELADO]: "bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-300",
  ISENTO: "bg-purple-200 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300",
};

export const FATURAMENTO_TIPO_COLOR_MAP: Record<FaturamentoTipo, string> = {
  [FaturamentoTipo.INTERNO]:
    "bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300",
  [FaturamentoTipo.REPASSE]:
    "bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-300",
  [FaturamentoTipo.INTEGRAL]: "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300",
};

export const FATURAMENTO_EMPRESA_COLOR_MAP: Record<Empresa, string> = {
  [Empresa.HUB_PROMO]: "bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300",
  [Empresa.HUB_MARKETING]:
    "bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300",
};

export const CONCILIACAO_STATUS_COLOR_MAP: Record<string, string> = {
  PENDENTE: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300",
  RESOLVIDO: "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300",
  DESCONTINUADA: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300",
  CANCELADO: "bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-300",
};

export const JOB_PLANEJAMENTO_STATUS_COLOR_MAP: Record<JobPlanejamentoStatus | "PENDENTE", string> =
  {
    PENDENTE: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300",

    [JobPlanejamentoStatus.AGUARDANDO_BRIEF_CLIENTE]:
      "bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300",

    [JobPlanejamentoStatus.COM_CRIACAO]:
      "bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300",

    [JobPlanejamentoStatus.CONCLUIDO]:
      "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300",

    [JobPlanejamentoStatus.EM_EXECUCAO]:
      "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300",

    [JobPlanejamentoStatus.STAND_BY]:
      "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300",

    [JobPlanejamentoStatus.AGUARDANDO_ESTUDO_VERBA]:
      "bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300",

    [JobPlanejamentoStatus.AGUARDANDO_PASSAGEM_INTERNA]:
      "bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-300",

    [JobPlanejamentoStatus.NAO_APLICAVEL]:
      "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300",
  };

export const TAREFA_STATUS_COLOR_MAP: Record<TarefaStatus, string> = {
  [TarefaStatus.ARQUIVADO]: "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300",
  [TarefaStatus.PENDENTE]:
    "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300",
  [TarefaStatus.EM_EXECUCAO]: "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300",
  [TarefaStatus.CONCLUIDO]: "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300",
  [TarefaStatus.CANCELADO]: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300",
};

export const TAREFA_TIPO_COLOR_MAP: Record<TarefaTipo, string> = {
  [TarefaTipo.AP_VALOR_ABAIXO_SALDO_UTILIZADO]:
    "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-500/10 dark:text-amber-400 dark:border-amber-500/20",
  // Financeiro/Fluxo de Caixa: Tons de Indigo (Sério e estável)
  [TarefaTipo.SOLICITAR_AP]:
    "bg-indigo-50 text-indigo-700 border-indigo-100 dark:bg-indigo-500/10 dark:text-indigo-400 dark:border-indigo-500/20",

  // Alerta/Atenção: Tons de Amber (Clássico para questionamentos, mas menos "sujo" que o orange)
  [TarefaTipo.QUESTIONAR_VERSAO]:
    "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-500/10 dark:text-amber-400 dark:border-amber-500/20",

  // Operacional/Ajuste: Violeta (Diferencia bem do azul padrão)
  [TarefaTipo.SOLICITAR_AJUSTE_PLANILHA_ATENDIMENTO]:
    "bg-violet-50 text-violet-700 border-violet-100 dark:bg-violet-500/10 dark:text-violet-400 dark:border-violet-500/20",

  // Sucesso/Novo Pedido: Esmeralda (Mais vibrante e limpo que o Green padrão)
  [TarefaTipo.SOLICITAR_ORCAMENTO_PRE_PRODUCAO]:
    "bg-emerald-50 text-emerald-700 border-emerald-100 dark:bg-emerald-500/10 dark:text-emerald-400 dark:border-emerald-500/20",

  // Revisão/Documento: Rose ou Pink (Para destacar que algo no arquivo precisa de olhar humano)
  [TarefaTipo.QUESTIONAR_ARQUIVO_APROVACAO]:
    "bg-rose-50 text-rose-700 border-rose-100 dark:bg-rose-500/10 dark:text-rose-400 dark:border-rose-500/20",

  // Facilities/Contato: Pink (data comemorativa, longe dos tons de alerta)
  [TarefaTipo.PEDIDO_BRINDE]:
    "bg-pink-50 text-pink-700 border-pink-100 dark:bg-pink-500/10 dark:text-pink-400 dark:border-pink-500/20",

  // Administrativo/Cadastro: Sky (Leve e profissional)
  [TarefaTipo.CRIAR_CLIENTE]:
    "bg-sky-50 text-sky-700 border-sky-100 dark:bg-sky-500/10 dark:text-sky-400 dark:border-sky-500/20",

  [TarefaTipo.SOLICITAR_CRIACAO_CLIENTE]:
    "bg-sky-50 text-sky-700 border-sky-100 dark:bg-sky-500/10 dark:text-sky-400 dark:border-sky-500/20",

  [TarefaTipo.QUESTIONAR_JOB]:
    "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-500/10 dark:text-amber-400 dark:border-amber-500/20",

  // Dúvidas sobre documentos do job (briefing/paper): mesma família âmbar dos questionamentos
  [TarefaTipo.QUESTIONAR_BRIEFING]:
    "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-500/10 dark:text-amber-400 dark:border-amber-500/20",

  [TarefaTipo.QUESTIONAR_PAPER]:
    "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-500/10 dark:text-amber-400 dark:border-amber-500/20",

  [TarefaTipo.SOLICITAR_AJUSTE_AP]:
    "bg-orange-50 text-orange-700 border-orange-100 dark:bg-orange-500/10 dark:text-orange-400 dark:border-orange-500/20",

  // Reprovação/Correção de faturamento: Red (sinaliza que algo foi reprovado e precisa de correção)
  [TarefaTipo.CORRIGIR_FATURAMENTO]:
    "bg-red-50 text-red-700 border-red-100 dark:bg-red-500/10 dark:text-red-400 dark:border-red-500/20",

  // Cobrança de aprovação de AP: mesma família âmbar dos alertas/cobranças
  [TarefaTipo.COBRAR_APROVACAO_AP]:
    "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-500/10 dark:text-amber-400 dark:border-amber-500/20",

  // Solicitação de desfaturamento: Amber (reversão que pede atenção do financeiro)
  [TarefaTipo.SOLICITAR_DESFATURAMENTO]:
    "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-500/10 dark:text-amber-400 dark:border-amber-500/20",

  // Tarefa genérica: Slate (neutra, criada livremente pelo usuário)
  [TarefaTipo.GENERICA]:
    "bg-slate-50 text-slate-700 border-slate-100 dark:bg-slate-500/10 dark:text-slate-400 dark:border-slate-500/20",

  // Upload de documentos do job (briefing/paper/estudo de verba): Teal (entrega de arquivo)
  [TarefaTipo.CRIAR_BRIEFING]:
    "bg-teal-50 text-teal-700 border-teal-100 dark:bg-teal-500/10 dark:text-teal-400 dark:border-teal-500/20",

  [TarefaTipo.CRIAR_PAPER]:
    "bg-teal-50 text-teal-700 border-teal-100 dark:bg-teal-500/10 dark:text-teal-400 dark:border-teal-500/20",

  [TarefaTipo.CRIAR_ESTUDO_VERBA]:
    "bg-teal-50 text-teal-700 border-teal-100 dark:bg-teal-500/10 dark:text-teal-400 dark:border-teal-500/20",
};

export const PROCESSAMENTO_IA_STATUS_COLOR_MAP: Record<StatusProcessamentoIA, string> = {
  PENDENTE: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300",
  CONCLUIDO: "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300",
  FALHA: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300",
};

export const ORDEM_JOB_COLOR_MAP: Record<PlanilhaOrdemJob, string> = {
  [PlanilhaOrdemJob.PRINCIPAL]: "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300",
  [PlanilhaOrdemJob.JOB_NOVO]:
    "bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-300",
  [PlanilhaOrdemJob.COMPLEMENTAR]:
    "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300",
};

export const COMPRA_TIPO_COLOR_MAP: Record<PlanilhaCompraTipo, string> = {
  [PlanilhaCompraTipo.NOVA]:
    "bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-300",
  [PlanilhaCompraTipo.ATRIBUIDA]:
    "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300",
  [PlanilhaCompraTipo.SEM_OPORTUNIDADE]:
    "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300",
};

export const ABRAGENCIA_NIVEL_COLOR_MAP: Record<AbrangenciaNivelEnum, string> = {
  [AbrangenciaNivelEnum.ALTA]: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300",
  [AbrangenciaNivelEnum.MEDIA]:
    "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300",
  [AbrangenciaNivelEnum.BAIXA]:
    "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300",
};

export const AP_STATUS_COLOR_MAP: Record<ApStatus, string> = {
  [ApStatus.AGUARDANDO_APROVACAO]:
    "bg-amber-50 text-amber-700 border border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800",
  [ApStatus.AGUARDANDO_APROVACAO_CLIENTE]:
    "bg-orange-50 text-orange-700 border border-orange-200 dark:bg-orange-900/30 dark:text-orange-300 dark:border-orange-800",
  [ApStatus.APROVADO]:
    "bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800",
  [ApStatus.APROVADO_NAO_FATURAR]:
    "bg-teal-50 text-teal-700 border border-teal-200 dark:bg-teal-900/30 dark:text-teal-300 dark:border-teal-800",
  [ApStatus.NAO_APROVADO]:
    "bg-red-50 text-red-700 border border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800",
  [ApStatus.SUSPENSO]:
    "bg-orange-50 text-orange-700 border border-orange-200 dark:bg-orange-900/30 dark:text-orange-300 dark:border-orange-800",
  [ApStatus.LIBERADO_FATURAMENTO]:
    "bg-sky-50 text-sky-700 border border-sky-200 dark:bg-sky-900/30 dark:text-sky-300 dark:border-sky-800",
  [ApStatus.FATURADO]:
    "bg-violet-50 text-violet-700 border border-violet-200 dark:bg-violet-900/30 dark:text-violet-300 dark:border-violet-800",
  [ApStatus.CANCELADO]:
    "bg-slate-100 text-slate-600 border border-slate-300 dark:bg-slate-800/50 dark:text-slate-400 dark:border-slate-700",
  [ApStatus.FECHADO]:
    "bg-slate-100 text-slate-600 border border-slate-300 dark:bg-slate-800/50 dark:text-slate-400 dark:border-slate-700",
  [ApStatus.REVISADO]:
    "bg-purple-50 text-purple-700 border border-purple-200 dark:bg-purple-900/30 dark:text-purple-300 dark:border-purple-800",
  [ApStatus.RECUSADO]:
    "bg-rose-50 text-rose-700 border border-rose-200 dark:bg-rose-900/30 dark:text-rose-300 dark:border-rose-800",
};

export const CUSTO_PROJETO_STATUS_COLOR_MAP: Record<CustoProjetoStatus, string> = {
  [CustoProjetoStatus.RASCUNHO]:
    "bg-slate-100 text-slate-700 border border-slate-300 dark:bg-slate-800/50 dark:text-slate-400 dark:border-slate-700",
  [CustoProjetoStatus.EM_APROVACAO]:
    "bg-amber-50 text-amber-700 border border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800",
  [CustoProjetoStatus.APROVADO]:
    "bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800",
  [CustoProjetoStatus.REPROVADO]:
    "bg-rose-50 text-rose-700 border border-rose-200 dark:bg-rose-900/30 dark:text-rose-300 dark:border-rose-800",
  [CustoProjetoStatus.CANCELADO]:
    "bg-zinc-100 text-zinc-600 border border-zinc-300 dark:bg-zinc-800/50 dark:text-zinc-400 dark:border-zinc-700",
};

export const CUSTO_BUDGET_VERSAO_STATUS_COLOR_MAP: Record<CustoBudgetVersaoStatus, string> = {
  [CustoBudgetVersaoStatus.RASCUNHO]:
    "bg-slate-100 text-slate-700 border border-slate-300 dark:bg-slate-800/50 dark:text-slate-400 dark:border-slate-700",
  [CustoBudgetVersaoStatus.EM_APROVACAO]:
    "bg-amber-50 text-amber-700 border border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800",
  [CustoBudgetVersaoStatus.APROVADO]:
    "bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800",
  [CustoBudgetVersaoStatus.REPROVADO]:
    "bg-rose-50 text-rose-700 border border-rose-200 dark:bg-rose-900/30 dark:text-rose-300 dark:border-rose-800",
};
